/**
 * PDF Template Management Component
 * Admin interface for managing PDF templates
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ActiveIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/apiClient';
import { useNotification } from '../../contexts/NotificationContext';
import ConfirmDialog from '../common/ConfirmDialog';
import useAsyncData from '../../hooks/useAsyncData';
import SortableHeaderCell from '../common/SortableHeaderCell';
import useTableSort from '../../hooks/useTableSort';

const PDF_TEMPLATE_COLUMNS = [
  { key: 'template_name', label: 'Template Name', align: 'left', getValue: (t) => t.template_name },
  { key: 'template_type', label: 'Type',          align: 'left', getValue: (t) => t.template_type },
  { key: 'is_active',     label: 'Status',        align: 'left', getValue: (t) => t.is_active ? 1 : 0 },
  { key: 'version',       label: 'Version',       align: 'left', getValue: (t) => t.version },
  { key: 'updated_at',    label: 'Last Updated',  align: 'left', getValue: (t) => t.updated_at },
  { key: 'updated_by',    label: 'Updated By',    align: 'left', getValue: (t) => t.updated_by_name },
];

function PDFTemplateManagement() {
  const notify = useNotification();
  const [filterType, setFilterType] = useState('all');
  const sort = useTableSort();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    template_name: '',
    template_type: 'contract',
    description: '',
    html_content: '',
  });

  const {
    data: templates,
    loading,
    error: fetchError,
    reload: reloadTemplates,
  } = useAsyncData(
    () => {
      const params = filterType !== 'all' ? `?template_type=${filterType}` : '';
      return apiGet(`/pdf-templates${params}`).then((res) => res.data || []);
    },
    { defaultValue: [], fetchOnMount: true, deps: [filterType], extractData: false }
  );

  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  // Surface fetch failures as a toast
  React.useEffect(() => {
    if (fetchError) notify.error(fetchError);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- notify is stable
  }, [fetchError]);

  // Preview state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [sampleData, setSampleData] = useState({});

  const handleOpenDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        template_name: template.template_name,
        template_type: template.template_type,
        description: template.description || '',
        html_content: template.html_content,
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        template_name: '',
        template_type: 'contract',
        description: '',
        html_content: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTemplate(null);
    setFormData({
      template_name: '',
      template_type: 'contract',
      description: '',
      html_content: '',
    });
  };

  const handleSaveTemplate = async () => {
    try {
      if (editingTemplate) {
        await apiPut(`/pdf-templates/${editingTemplate.id}`, formData);
      } else {
        await apiPost('/pdf-templates', formData);
      }
      handleCloseDialog();
      reloadTemplates();
    } catch (err) {
      console.error('Error saving template:', err);
      notify.error('Failed to save template');
    }
  };

  const handleActivateTemplate = async (id) => {
    try {
      await apiPost(`/pdf-templates/${id}/activate`);
      reloadTemplates();
    } catch (err) {
      console.error('Error activating template:', err);
      notify.error('Failed to activate template');
    }
  };

  const handleDeleteTemplate = (id) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Template',
      message: 'Are you sure you want to delete this template?',
      onConfirm: async () => {
        try {
          await apiDelete(`/pdf-templates/${id}`);
          setConfirmDialog(prev => ({ ...prev, open: false }));
          reloadTemplates();
        } catch (err) {
          console.error('Error deleting template:', err);
          setConfirmDialog(prev => ({ ...prev, open: false }));
          notify.error(err.response?.data?.error?.message || err.message || 'Failed to delete template');
        }
      },
    });
  };

  // Get default sample data based on template type
  const getDefaultSampleData = (templateType) => {
    const today = new Date().toISOString().split('T')[0];
    const futureDate = new Date();
    futureDate.setMonth(futureDate.getMonth() + 12);
    const maturityDate = futureDate.toISOString().split('T')[0];

    switch (templateType) {
      case 'contract':
        return {
          contractReference: 'TEST-2024-001',
          contractDate: today,
          status: 'Draft',
          investorName: 'John Doe',
          investorEmail: 'john.doe@example.com',
          investorPhone: '+1 234 567 8900',
          productName: 'Fixed Term Investment',
          productDescription: 'A secure fixed-term investment product with guaranteed returns',
          capitalAmount: '100000.00',
          interestRate: '8.50',
          rateType: 'Fixed',
          currencyCode: 'USD',
          currencyName: 'US Dollar',
          depositDate: today,
          maturityDate: maturityDate,
          term: '12',
          termUnit: 'Months',
          brokerName: 'Jane Smith',
          brokerEmail: 'jane.smith@broker.com',
          brokerFee: '2.00',
          collateralType: 'Property',
          collateralValue: '150000.00',
          collateralDescription: 'Residential property in downtown',
          collateralProviderName: 'John Doe',
          notes: 'This is a sample contract for preview purposes.',
          generatedDate: new Date().toLocaleString(),
        };
      case 'amortization_schedule':
        return {
          contractReference: 'TEST-2024-001',
          investorName: 'John Doe',
          productName: 'Fixed Term Investment',
          capitalAmount: '100000.00',
          interestRate: '8.50',
          rateType: 'Fixed',
          currencyCode: 'USD',
          depositDate: today,
          maturityDate: maturityDate,
          term: '12',
          termUnit: 'Months',
          scheduleItems: [
            {
              sequence: 1,
              paymentDate: '2024-02-01',
              capitalAmount: '8333.33',
              interestAmount: '708.33',
              totalPayment: '9041.66',
              outstandingBalance: '91666.67',
              narration: 'Monthly payment 1 of 12',
            },
            {
              sequence: 2,
              paymentDate: '2024-03-01',
              capitalAmount: '8333.33',
              interestAmount: '649.31',
              totalPayment: '8982.64',
              outstandingBalance: '83333.34',
              narration: 'Monthly payment 2 of 12',
            },
            {
              sequence: 3,
              paymentDate: '2024-04-01',
              capitalAmount: '8333.34',
              interestAmount: '590.28',
              totalPayment: '8923.62',
              outstandingBalance: '75000.00',
              narration: 'Monthly payment 3 of 12',
            },
          ],
          totalCapital: '100000.00',
          totalInterest: '8500.00',
          totalPayment: '108500.00',
          paymentCount: '12',
          generatedDate: new Date().toLocaleString(),
        };
      case 'report':
        return {
          reportTitle: 'Sample Report',
          reportDate: today,
          reportType: 'Performance Report',
          description: 'This is a sample report for preview purposes.',
          generatedDate: new Date().toLocaleString(),
        };
      default:
        return {};
    }
  };

  // Handle opening preview dialog
  const handleOpenPreview = (template) => {
    setPreviewTemplate(template);
    setSampleData(getDefaultSampleData(template.template_type));
    setPreviewPdfUrl(null);
    setPreviewDialogOpen(true);
  };

  // Handle closing preview dialog
  const handleClosePreview = () => {
    setPreviewDialogOpen(false);
    setPreviewTemplate(null);
    setPreviewPdfUrl(null);
    setSampleData({});
  };

  // Generate preview PDF
  const handleGeneratePreview = async () => {
    try {
      setPreviewLoading(true);

      // If we're previewing from the editor (unsaved), use the form data
      const templateId = previewTemplate?.id;
      const payload = templateId
        ? { template_id: templateId, sample_data: sampleData }
        : {
            html_content: formData.html_content,
            template_type: formData.template_type,
            sample_data: sampleData
          };

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/generate-pdf/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to generate preview');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
    } catch (err) {
      console.error('Error generating preview:', err);
      notify.error('Failed to generate preview: ' + err.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Handle preview from editor
  const handlePreviewFromEditor = () => {
    if (!formData.html_content) {
      notify.error('Please enter HTML content first');
      return;
    }
    setPreviewTemplate({ template_type: formData.template_type });
    setSampleData(getDefaultSampleData(formData.template_type));
    setPreviewPdfUrl(null);
    setPreviewDialogOpen(true);
  };

  // Update sample data field
  const handleSampleDataChange = (field, value) => {
    setSampleData({ ...sampleData, [field]: value });
  };

  const templateTypes = ['all', 'contract', 'amortization_schedule', 'report'];

  const getStatusChip = (isActive) => {
    return isActive ? (
      <Chip icon={<ActiveIcon />} label="Active" color="success" size="small" />
    ) : (
      <Chip label="Inactive" color="default" size="small" />
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">PDF Template Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          New Template
        </Button>
      </Box>
      {/* Filter Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={filterType}
          onChange={(e, value) => setFilterType(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {templateTypes.map((type) => (
            <Tab
              key={type}
              label={type === 'all' ? 'All Templates' : type.replace(/_/g, ' ').toUpperCase()}
              value={type}
            />
          ))}
        </Tabs>
      </Paper>
      {/* Templates Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {PDF_TEMPLATE_COLUMNS.map((col) => (
                <SortableHeaderCell key={col.key} sort={sort} columnKey={col.key} align={col.align}>
                  {col.label}
                </SortableHeaderCell>
              ))}
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary">No templates found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              sort.sortRows(templates, PDF_TEMPLATE_COLUMNS).map((template) => (
                <TableRow key={template.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {template.template_name}
                    </Typography>
                    {template.description && (
                      <Typography variant="caption" color="textSecondary">
                        {template.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={template.template_type.replace(/_/g, ' ')}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{getStatusChip(template.is_active)}</TableCell>
                  <TableCell>v{template.version}</TableCell>
                  <TableCell>
                    {format(new Date(template.updated_at), 'dd MMM yyyy HH:mm')}
                  </TableCell>
                  <TableCell>{template.updated_by_name || 'System'}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenPreview(template)}
                      title="Preview"
                      color="primary"
                    >
                      <PreviewIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(template)}
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {!template.is_active && (
                      <IconButton
                        size="small"
                        onClick={() => handleActivateTemplate(template.id)}
                        title="Activate"
                        color="success"
                      >
                        <ActiveIcon fontSize="small" />
                      </IconButton>
                    )}
                    {!template.is_active && (
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteTemplate(template.id)}
                        title="Delete"
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Template Editor Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingTemplate ? 'Edit Template' : 'New Template'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Template Name"
                value={formData.template_name}
                onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                required
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                select
                label="Template Type"
                value={formData.template_type}
                onChange={(e) => setFormData({ ...formData, template_type: e.target.value })}
                SelectProps={{ native: true }}
                required
              >
                <option value="contract">Contract</option>
                <option value="amortization_schedule">Amortization Schedule</option>
                <option value="report">Report</option>
              </TextField>
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="HTML Content"
                value={formData.html_content}
                onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
                multiline
                rows={15}
                required
                helperText="Enter HTML template with Handlebars placeholders"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handlePreviewFromEditor}
            startIcon={<PreviewIcon />}
            disabled={!formData.html_content}
          >
            Preview
          </Button>
          <Button
            onClick={handleSaveTemplate}
            variant="contained"
            disabled={!formData.template_name || !formData.html_content}
          >
            {editingTemplate ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Template Preview
          {previewTemplate && (
            <Typography variant="caption" display="block" color="textSecondary">
              {previewTemplate.template_name || `${formData.template_type} template`}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            {/* Sample Data Form */}
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Paper sx={{ p: 2, maxHeight: '70vh', overflow: 'auto' }}>
                <Typography variant="h6" gutterBottom>
                  Sample Data
                </Typography>
                <Typography variant="caption" color="textSecondary" paragraph>
                  Edit the sample data below to see how the template renders
                </Typography>

                {previewTemplate?.template_type === 'contract' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Contract Reference"
                      value={sampleData.contractReference || ''}
                      onChange={(e) => handleSampleDataChange('contractReference', e.target.value)}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Investor Name"
                      value={sampleData.investorName || ''}
                      onChange={(e) => handleSampleDataChange('investorName', e.target.value)}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Investor Email"
                      value={sampleData.investorEmail || ''}
                      onChange={(e) => handleSampleDataChange('investorEmail', e.target.value)}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Product Name"
                      value={sampleData.productName || ''}
                      onChange={(e) => handleSampleDataChange('productName', e.target.value)}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Capital Amount"
                      value={sampleData.capitalAmount || ''}
                      onChange={(e) => handleSampleDataChange('capitalAmount', e.target.value)}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Interest Rate"
                      value={sampleData.interestRate || ''}
                      onChange={(e) => handleSampleDataChange('interestRate', e.target.value)}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Currency Code"
                      value={sampleData.currencyCode || ''}
                      onChange={(e) => handleSampleDataChange('currencyCode', e.target.value)}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Deposit Date"
                      type="date"
                      value={sampleData.depositDate || ''}
                      onChange={(e) => handleSampleDataChange('depositDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Maturity Date"
                      type="date"
                      value={sampleData.maturityDate || ''}
                      onChange={(e) => handleSampleDataChange('maturityDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                )}

                {previewTemplate?.template_type === 'amortization_schedule' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Contract Reference"
                      value={sampleData.contractReference || ''}
                      onChange={(e) => handleSampleDataChange('contractReference', e.target.value)}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Investor Name"
                      value={sampleData.investorName || ''}
                      onChange={(e) => handleSampleDataChange('investorName', e.target.value)}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Product Name"
                      value={sampleData.productName || ''}
                      onChange={(e) => handleSampleDataChange('productName', e.target.value)}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Capital Amount"
                      value={sampleData.capitalAmount || ''}
                      onChange={(e) => handleSampleDataChange('capitalAmount', e.target.value)}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Interest Rate"
                      value={sampleData.interestRate || ''}
                      onChange={(e) => handleSampleDataChange('interestRate', e.target.value)}
                    />
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Schedule items are pre-populated with sample data
                    </Alert>
                  </Box>
                )}

                {previewTemplate?.template_type === 'report' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Report Title"
                      value={sampleData.reportTitle || ''}
                      onChange={(e) => handleSampleDataChange('reportTitle', e.target.value)}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Report Type"
                      value={sampleData.reportType || ''}
                      onChange={(e) => handleSampleDataChange('reportType', e.target.value)}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Description"
                      value={sampleData.description || ''}
                      onChange={(e) => handleSampleDataChange('description', e.target.value)}
                      multiline
                      rows={3}
                    />
                  </Box>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleGeneratePreview}
                  disabled={previewLoading}
                  sx={{ mt: 2 }}
                  startIcon={previewLoading ? <CircularProgress size={20} /> : <PreviewIcon />}
                >
                  {previewLoading ? 'Generating...' : 'Generate Preview'}
                </Button>
              </Paper>
            </Grid>

            {/* PDF Preview */}
            <Grid
              size={{
                xs: 12,
                md: 8
              }}>
              <Paper sx={{ p: 2, height: '70vh', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" gutterBottom>
                  PDF Preview
                </Typography>
                {!previewPdfUrl && !previewLoading && (
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Typography color="textSecondary">
                      Click "Generate Preview" to see the PDF
                    </Typography>
                  </Box>
                )}
                {previewLoading && (
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )}
                {previewPdfUrl && (
                  <Box sx={{ flex: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <iframe
                      src={previewPdfUrl}
                      style={{ width: '100%', height: '100%', border: 'none' }}
                      title="PDF Preview"
                    />
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Confirm"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
}

export default PDFTemplateManagement;
